// src/utils/lazyPage.js
// Loads a page's code only when that page is first shown.
//
// Why: every page used to ride one 5.4 MB startup file, so a phone downloaded and
// processed all 31 pages plus the spreadsheet and PDF tools before it could show
// anything. Each page is now its own small file, fetched when it is opened (or
// ahead of time with Page.preload()).
//
// Uploads: a phone still running the previous version can ask for a page file
// the new upload replaced. Webpack reports that as a ChunkLoadError, so the app
// reloads once to pick up the new version instead of leaving a dead screen. If a
// reload moments ago did not fix it, the error is let through, so it never loops.
//
// No signal (09/16/2026, third review round): a page file that could not be fetched
// because the phone is offline is not a missing file, and reloading then only swaps
// the loading screen for the browser's "not connected" page, which has no buttons.
// So while the browser says it is offline the loading screen stays up, and the
// download is tried again once the signal is back (or the app is brought back to
// the screen with a signal, since iOS may not pass on the "online" event while the
// app was in the background). Only a retry that still fails reloads, as above.
//
// Fourth review round, same day:
//   - Patient tries. The first moment the signal is back is when a returning connection is
//     least settled, and one failed try there used to reload into the same dead end. Now the
//     download is tried up to three times: at once, about 2 seconds later, and about 5 seconds
//     after that. A try that fails because the signal dropped again (or finds it gone after a
//     pause) does not count: it waits for the signal again and then makes that same try.
//   - A count of the pages someone opened that are still loading, for App's navy bar. React Router
//     runs every move between pages as a transition, and React then keeps the old page up while the
//     new one waits, so no loading screen appears and the tap looks ignored; a pop-up form behind an
//     empty loading screen shows nothing at all. A download only preloaded (at launch, say) is not
//     counted until something on screen asks for that page: nobody opened anything yet.
//
// Fifth review round, same day:
//   - A page stays counted from its first failed download until it arrives or fails for good, the
//     tries after the signal returns included (a slow returning download used to leave the old page
//     up with no word at all). The count also says which phase each load is in: waiting for a usable
//     connection, or being tried now, so the bar can say "No signal" or "Loading".
//   - forgetWaitingPages(): App calls it when the person signs out or the screen moves to another
//     address (React says nothing when it drops a move the person replaced). The loads carry on
//     quietly, and are counted again only if something on screen asks for that page again.
//   - "A usable connection" means the browser says online, the app is on screen, AND our own site
//     answers (utils/siteReachable.js). The browser's flag alone can say online on one bar that passes
//     nothing, and a reload there is the dead end above. So the wait checks the site on "online", when
//     the app comes back to the screen, and every 15 seconds (a flag can come back with no event), and
//     no reload happens unless the site has just answered: otherwise the page goes back to waiting.
//   - A first download that is simply slow (final review of that round). On a connection the browser
//     calls online but that passes nothing, a download does not fail, it hangs, and webpack waits 120
//     seconds before calling it failed. So an opened page whose first download has not arrived after
//     1.5 seconds is counted as being tried, and the bar says "Loading what you opened..." until it
//     arrives or fails. A download that arrives sooner never shows the bar. While that first try is
//     out, its phase follows the browser's online and offline events, so the bar never says "No
//     signal" with the signal back, or the reverse. A `quiet` piece (a part of a page that is already
//     on screen, such as a date picker) is never counted: the page it belongs to is already up.
//
// A change still saving (10/05/2026, utils/saveWhenConnected.js): the reload after an upload waits
// until no change is waiting for the signal, so it never asks "leave this page?" (a No there used to
// leave this page on its loading screen for good) and never drops the change. The loading screen
// stays up meanwhile, and the navy bar says the change is waiting.
//
// This file is part of the download every launch needs, so it imports nothing but React, the site
// check, which itself imports nothing, and the save count, which imports only the site check.
import { createElement, lazy } from "react";
import { canReachSite } from "./siteReachable";
import { whenNoSavesWaiting } from "./saveWhenConnected";

const RELOAD_KEY = "tes:pageReloadAt";
const RELOAD_COOLDOWN_MS = 30 * 1000;

// A reload already waiting for a change to save: every page that fails meanwhile waits for the same one.
let reloadWaitingForSave = false;

// The pause before each try once the signal is back: at once, then about 2 seconds, then about 5.
export const SIGNAL_RETRY_PAUSES_MS = [0, 2000, 5000];

// How often a wait for the signal asks the site by itself, in case no event says the signal is back.
export const SIGNAL_CHECK_EVERY_MS = 15 * 1000;

// How long an opened page's first download may take before the bar says it is loading.
export const SLOW_FIRST_TRY_MS = 1500;

// The two phases of an opened page that is still loading.
export const WAITING_FOR_SIGNAL = "signal";
export const TRYING_NOW = "trying";

// ---- The shared count ------------------------------------------------------------------------------
//
// Every load that counts right now, each with its phase. A load counts while it is loading AND something
// on screen asked for its page after the last time App said to forget. "After" is told by marks: each
// time a page is drawn it takes the next mark, and forgetWaitingPages(upTo) forgets every page whose
// latest mark is not above upTo. App passes the mark it read when it drew the screen that just appeared,
// so a page drawn as part of that very screen (a dashboard's first page, say) is never forgotten.
const countedLoads = new Set();
let waitingForSignal = 0;
let tryingNow = 0;
let latestMark = 0;
let forgottenUpTo = 0;
const waitingListeners = new Set();

function publishCounts() {
  let signal = 0;
  let trying = 0;
  countedLoads.forEach((load) => {
    if (load.phase === WAITING_FOR_SIGNAL) signal += 1;
    else trying += 1;
  });
  if (signal === waitingForSignal && trying === tryingNow) return;
  waitingForSignal = signal;
  tryingNow = trying;
  waitingListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // One listener's fault must never stop a page from loading or the others from hearing.
    }
  });
}

// Puts one load in or out of the count, as its phase and its mark say now.
function recount(load) {
  if (!load.quiet && load.phase !== null && load.openedMark > forgottenUpTo) countedLoads.add(load);
  else countedLoads.delete(load);
  publishCounts();
}

// How many opened pages are still loading, in either phase.
export function waitingPageCount() {
  return waitingForSignal + tryingNow;
}

// "signal" while any opened page waits for a usable connection, otherwise "trying" while any is being
// tried now, otherwise null. A plain string, so React's useSyncExternalStore can read it directly.
export function waitingPagePhase() {
  if (waitingForSignal > 0) return WAITING_FOR_SIGNAL;
  if (tryingNow > 0) return TRYING_NOW;
  return null;
}

// Shaped for React's useSyncExternalStore: the listener is called with nothing, and the
// returned function stops it.
export function subscribeWaitingPages(listener) {
  waitingListeners.add(listener);
  return () => {
    waitingListeners.delete(listener);
  };
}

// The mark of the latest page drawn so far. Read by App while it draws.
export function pageOpenMark() {
  return latestMark;
}

// Takes every page drawn up to `upTo` (by default, every page drawn so far) out of the count. Their
// loads carry on, uncounted, whatever happens to them next, unless their page is drawn again. The count
// is made from the loads themselves, so it can never go below zero.
export function forgetWaitingPages(upTo = latestMark) {
  forgottenUpTo = Math.max(forgottenUpTo, upTo);
  countedLoads.forEach((load) => {
    if (load.openedMark <= forgottenUpTo) countedLoads.delete(load);
  });
  publishCounts();
}

// ---- The signal ------------------------------------------------------------------------------------

function sessionStore() {
  try {
    return window.sessionStorage;
  } catch {
    return null; // storage blocked (some private modes)
  }
}

// Webpack names JS failures ChunkLoadError; CSS chunk failures only say so in the message.
export function isChunkLoadError(err) {
  return (
    err?.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(String(err?.message || ""))
  );
}

// The browser's own word that it has no connection. Only a definite "no" counts. Shared with App.js
// and the no-signal notes, which are in the launch download too.
export function browserIsOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

const appIsHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

// Calls onUsable once, the first time the browser says it is online, the app is on screen, and our own
// site answers. It asks on "online", when the app comes back to the screen, and every 15 seconds while
// waiting. Returns a function that calls the wait off. Also used by App.js for its reload after a sign-out.
export function whenSignalIsUsable(onUsable, { canReach = canReachSite, every = SIGNAL_CHECK_EVERY_MS } = {}) {
  let over = false;
  let asking = false;
  let askAgain = false;
  let timer = null;
  const stop = () => {
    over = true;
    window.removeEventListener("online", check);
    document.removeEventListener("visibilitychange", check);
    clearInterval(timer);
  };
  async function check() {
    if (over || browserIsOffline() || appIsHidden()) return;
    // One question at a time; something that happens meanwhile asks once more if that one found no answer.
    if (asking) {
      askAgain = true;
      return;
    }
    asking = true;
    let answered = false;
    try {
      answered = (await canReach()) === true;
    } catch {
      answered = false;
    }
    asking = false;
    if (over) return;
    if (answered && !browserIsOffline() && !appIsHidden()) {
      stop();
      onUsable();
      return;
    }
    if (askAgain) {
      askAgain = false;
      check();
    }
  }
  window.addEventListener("online", check);
  document.addEventListener("visibilitychange", check);
  timer = setInterval(check, every);
  return stop;
}

// Every download waiting with the site's own check shares one wait, so each "online", each return to the app
// and each 15 second tick asks the site once, however many downloads are waiting (a launch that lost its
// signal can have several).
let sharedWait = null;
function waitForAUsableSignal() {
  if (!sharedWait) {
    sharedWait = new Promise((resolve) => {
      whenSignalIsUsable(() => {
        sharedWait = null;
        resolve();
      });
    });
  }
  return sharedWait;
}

const pauseFor = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// ---- One page --------------------------------------------------------------------------------------

export function lazyPage(
  importPage,
  {
    storage = sessionStore(),
    reload = () => window.location.reload(),
    now = Date.now,
    isOffline = browserIsOffline,
    canReach = canReachSite,
    whenOnline = canReach === canReachSite
      ? waitForAUsableSignal
      : () =>
          new Promise((resolve) => {
            whenSignalIsUsable(resolve, { canReach });
          }),
    retryPauses = SIGNAL_RETRY_PAUSES_MS,
    pause = pauseFor,
    slowAfter = SLOW_FIRST_TRY_MS,
    quiet = false,
  } = {},
) {
  // Without storage there is no way to remember a reload already happened, so
  // never reload rather than risk a loop. A change still waiting for the signal is let
  // land first (see the top of this file).
  const reloadOnce = () => {
    if (!storage) return false;
    if (reloadWaitingForSave) return true;
    try {
      const last = Number(storage.getItem(RELOAD_KEY)) || 0;
      if (now() - last <= RELOAD_COOLDOWN_MS) return false;
      storage.setItem(RELOAD_KEY, String(now()));
    } catch {
      return false;
    }
    // At once when nothing is waiting to save (as always); otherwise once the last change lands.
    reloadWaitingForSave = true;
    whenNoSavesWaiting(() => {
      reloadWaitingForSave = false;
      try {
        storage.setItem(RELOAD_KEY, String(now()));
      } catch {
        // The stamp written above still stands.
      }
      reload();
    });
    return true;
  };

  // A download that failed for good. A missing file reloads the app once, and the loading
  // screen stays up while it does; anything else is let through.
  const giveUp = (err) => {
    if (isChunkLoadError(err) && reloadOnce()) return new Promise(() => {});
    throw err;
  };

  // This page's place in the shared count: its phase while it loads (null otherwise), and the mark it
  // took the last time it was drawn (0 until it is).
  const load = { phase: null, openedMark: 0, quiet };
  const setPhase = (phase) => {
    if (load.phase === phase) return;
    load.phase = phase;
    recount(load);
  };

  // Does our own site answer right now? Never asked while the browser says it is offline.
  const siteAnswers = async () => {
    if (isOffline()) return false;
    try {
      return (await canReach()) === true;
    } catch {
      return false;
    }
  };

  const waitForTheSignal = async () => {
    setPhase(WAITING_FOR_SIGNAL);
    await whenOnline();
    setPhase(TRYING_NOW);
  };

  // Always at least one try, whatever the pauses say.
  const pauses = retryPauses.length ? retryPauses : [0];

  // Waits for a usable connection, then makes the patient tries. Resolves with { page } when one
  // arrives, or { failure } when all of them failed with a signal.
  const patientTries = async () => {
    await waitForTheSignal();
    let failedTries = 0;
    let failure = null;
    while (failedTries < pauses.length) {
      const wait = pauses[failedTries];
      if (wait > 0) {
        await pause(wait);
        // The signal went again during the pause: wait for it, then this same try.
        if (isOffline()) {
          await waitForTheSignal();
          continue;
        }
      }
      try {
        return { page: await importPage() };
      } catch (err) {
        if (!isChunkLoadError(err)) throw err;
        // Failed because the signal went again: not a try. Webpack forgets a failed file, so
        // the next try really fetches it again.
        if (isOffline()) {
          await waitForTheSignal();
          continue;
        }
        failedTries += 1;
        failure = err;
      }
    }
    return { failure };
  };

  // After a failed download: reload (or give up) only once our own site has just answered; until then,
  // wait for a usable connection and make the patient tries, as often as it takes.
  const recover = async (firstFailure) => {
    let failure = firstFailure;
    setPhase(isOffline() ? WAITING_FOR_SIGNAL : TRYING_NOW);
    for (;;) {
      if (await siteAnswers()) {
        setPhase(null);
        return giveUp(failure);
      }
      const outcome = await patientTries();
      if ("page" in outcome) {
        setPhase(null);
        return outcome.page;
      }
      failure = outcome.failure;
    }
  };

  let pending = null;
  const startLoading = () => {
    if (!pending) {
      pending = (async () => {
        // A first download still out after a moment counts as being tried (or as waiting, with no
        // signal), so a hung download is never silent. Only an opened page is counted (recount).
        let firstTryOver = false;
        const followSignal = () => {
          if (!firstTryOver && load.phase !== null) {
            setPhase(isOffline() ? WAITING_FOR_SIGNAL : TRYING_NOW);
          }
        };
        let listening = false;
        const slowTimer = Number.isFinite(slowAfter)
          ? setTimeout(() => {
              if (firstTryOver || load.phase !== null) return;
              setPhase(isOffline() ? WAITING_FOR_SIGNAL : TRYING_NOW);
              window.addEventListener("online", followSignal);
              window.addEventListener("offline", followSignal);
              listening = true;
            }, Math.max(0, slowAfter))
          : null;
        const firstTryEnds = () => {
          firstTryOver = true;
          clearTimeout(slowTimer);
          if (listening) {
            window.removeEventListener("online", followSignal);
            window.removeEventListener("offline", followSignal);
          }
        };
        let page;
        try {
          page = await importPage();
        } catch (err) {
          firstTryEnds();
          if (!isChunkLoadError(err)) {
            setPhase(null);
            throw err;
          }
          try {
            return await recover(err);
          } catch (lastErr) {
            setPhase(null);
            throw lastErr;
          }
        }
        firstTryEnds();
        setPhase(null);
        return page;
      })().catch((err) => {
        pending = null; // a later attempt downloads again
        throw err;
      });
    }
    return pending;
  };

  // React calls this once, the first time the page is drawn, and keeps what it returns.
  const LoadedPage = lazy(startLoading);

  // The page as the app uses it. Drawing it marks it as asked for, every time: React only asks for the
  // code once, so this is how a page opened again after being forgotten is counted again. The count
  // changes after React has finished drawing, never in the middle of it: telling App then would be an
  // update to one component while React draws another.
  function Page(props) {
    latestMark += 1;
    load.openedMark = latestMark;
    if (load.phase !== null) Promise.resolve().then(() => recount(load));
    return createElement(LoadedPage, props);
  }
  Page.displayName = "LazyPage";
  Page.preload = () => {
    startLoading().catch(() => {});
  };
  return Page;
}
